import type { LevelId } from './sequence';

// The tagged beat bank — the engine picks beats whose `scramblePattern` is due.
// Each beat: the Goblin swapped ONE word for a wrong-but-decodable distractor that
// breaks the meaning; the child catches it, then REBUILDS the correct word (whose
// pattern is the one being practiced). Every word is decodable at/below the beat's
// level (enforced by validate.ts). The broken word's POSITION is deliberately varied
// across beats (not always last) so he must read to find it, not pattern-match.

export interface Beat {
  id: string;
  level: LevelId;
  /** Pattern id (see sequence.ts) that the correctWord exercises. */
  scramblePattern: string;
  /** Displayed (scrambled) sentence tokens. */
  words: string[];
  /** Index of the Goblin's broken word. */
  brokenIndex: number;
  /** The word that fixes the meaning; the child rebuilds it. */
  correctWord: string;
  /** Grapheme tiles of the correct word (digraphs stay together). */
  correctGraphemes: string[];
  payoffEmoji: string;
}

/** Stable audio id for a word clip (lowercased, letters only). */
export function wordAudioId(word: string): string {
  return `w_${word.toLowerCase().replace(/[^a-z]/g, '')}`;
}

const b = (
  id: string,
  level: LevelId,
  scramblePattern: string,
  words: string[],
  brokenIndex: number,
  correctWord: string,
  correctGraphemes: string[],
  payoffEmoji: string,
): Beat => ({ id, level, scramblePattern, words, brokenIndex, correctWord, correctGraphemes, payoffEmoji });

export const BEATS: Beat[] = [
  // ---- CVC: short vowels ---- (broken-word position varies: early/mid/late)
  b('a1', 'cvc', 'short_a', ['The', 'cot', 'sat', 'on', 'a', 'mat'], 1, 'cat', ['c', 'a', 't'], '🐱'),
  b('a2', 'cvc', 'short_a', ['I', 'put', 'my', 'cap', 'in', 'the', 'bug'], 6, 'bag', ['b', 'a', 'g'], '🎒'),
  b('e1', 'cvc', 'short_e', ['Spidey', 'has', 'a', 'bed', 'on', 'his', 'leg'], 3, 'web', ['w', 'e', 'b'], '🕸️'),
  b('e2', 'cvc', 'short_e', ['A', 'bug', 'bit', 'my', 'bed'], 4, 'leg', ['l', 'e', 'g'], '🦵'),
  b('i1', 'cvc', 'short_i', ['Put', 'the', 'cat', 'on', 'the', 'pot'], 2, 'lid', ['l', 'i', 'd'], '🥘'),
  b('i2', 'cvc', 'short_i', ['The', 'big', 'pin', 'is', 'in', 'mud'], 2, 'pig', ['p', 'i', 'g'], '🐷'),
  b('o1', 'cvc', 'short_o', ['Put', 'the', 'fox', 'on', 'top', 'of', 'the', 'bus'], 2, 'box', ['b', 'o', 'x'], '📦'),
  b('o2', 'cvc', 'short_o', ['The', 'log', 'can', 'run', 'in', 'mud'], 1, 'dog', ['d', 'o', 'g'], '🐕'),
  b('u1', 'cvc', 'short_u', ['I', 'put', 'a', 'pup', 'on', 'the', 'mat'], 3, 'cup', ['c', 'u', 'p'], '🥤'),
  b('u2', 'cvc', 'short_u', ['I', 'sit', 'on', 'the', 'bug', 'to', 'nap'], 4, 'bus', ['b', 'u', 's'], '🚌'),

  // ---- Digraphs ---- (Chapter 2: Doc Ock)
  b('sh1', 'digraphs', 'sh', ['A', 'duck', 'ran', 'to', 'the', 'fish'], 5, 'shed', ['sh', 'e', 'd'], '🏚️'),
  b('sh2', 'digraphs', 'sh', ['I', 'can', 'get', 'a', 'shed', 'in', 'the', 'net'], 4, 'fish', ['f', 'i', 'sh'], '🐟'),
  b('sh3', 'digraphs', 'sh', ['Dad', 'will', 'ship', 'for', 'a', 'cap'], 2, 'shop', ['sh', 'o', 'p'], '🛒'),
  b('ch1', 'digraphs', 'ch', ['His', 'chip', 'has', 'mud', 'on', 'it'], 1, 'chin', ['ch', 'i', 'n'], '🙂'),
  b('ch2', 'digraphs', 'ch', ['Dad', 'will', 'shop', 'the', 'log'], 2, 'chop', ['ch', 'o', 'p'], '🪓'),
  b('ch3', 'digraphs', 'ch', ['We', 'can', 'chop', 'on', 'the', 'bus'], 2, 'chat', ['ch', 'a', 't'], '💬'),
  b('th1', 'digraphs', 'th', ['A', 'fish', 'sat', 'on', 'the', 'web'], 1, 'moth', ['m', 'o', 'th'], '🦋'),
  b('th2', 'digraphs', 'th', ['We', 'ran', 'up', 'the', 'pan'], 4, 'path', ['p', 'a', 'th'], '🛤️'),
  b('th3', 'digraphs', 'th', ['The', 'path', 'is', 'by', 'the', 'den'], 1, 'bath', ['b', 'a', 'th'], '🛁'),
  b('ck1', 'digraphs', 'ck', ['He', 'put', 'a', 'duck', 'in', 'the', 'sock'], 3, 'rock', ['r', 'o', 'ck'], '🪨'),
  b('ck2', 'digraphs', 'ck', ['Put', 'the', 'deck', 'in', 'the', 'mud'], 2, 'duck', ['d', 'u', 'ck'], '🦆'),
  b('wh1', 'digraphs', 'wh', ['Then', 'will', 'we', 'win'], 0, 'when', ['wh', 'e', 'n'], '🤔'),
  b('wh2', 'digraphs', 'wh', ['Spidey', 'can', 'ship', 'up', 'the', 'web'], 2, 'whiz', ['wh', 'i', 'z'], '💨'),
  b('ng1', 'digraphs', 'ng', ['Put', 'the', 'wing', 'in', 'the', 'bag'], 2, 'ring', ['r', 'i', 'ng'], '💍'),
  b('ng2', 'digraphs', 'ng', ['We', 'song', 'in', 'the', 'sun'], 1, 'sing', ['s', 'i', 'ng'], '🎤'),

  // ---- Blends ---- (Chapter 3: Rhino)
  b('st1', 'blends', 'st', ['The', 'hen', 'sat', 'on', 'a', 'nut'], 5, 'nest', ['n', 'e', 's', 't'], '🪺'),
  b('nd1', 'blends', 'nd', ['The', 'band', 'is', 'wet', 'and', 'hot'], 1, 'sand', ['s', 'a', 'n', 'd'], '🏖️'),
  b('bl1', 'blends', 'bl', ['The', 'club', 'is', 'on', 'the', 'mat'], 1, 'blob', ['bl', 'o', 'b'], '🫧'),
  b('bl2', 'blends', 'bl', ['The', 'blob', 'van', 'is', 'fast'], 1, 'black', ['bl', 'a', 'ck'], '🚐'),
  b('cl1', 'blends', 'cl', ['We', 'clip', 'for', 'Spidey'], 1, 'clap', ['cl', 'a', 'p'], '👏'),
  b('cl2', 'blends', 'cl', ['Put', 'a', 'clop', 'on', 'it'], 2, 'clip', ['cl', 'i', 'p'], '📎'),
  b('fl1', 'blends', 'fl', ['Spidey', 'put', 'the', 'flap', 'on', 'top'], 3, 'flag', ['fl', 'a', 'g'], '🚩'),
  b('fl2', 'blends', 'fl', ['We', 'can', 'flag', 'and', 'run'], 2, 'flip', ['fl', 'i', 'p'], '🤸'),
  b('gr1', 'blends', 'gr', ['Spidey', 'can', 'grin', 'the', 'bug'], 2, 'grab', ['gr', 'a', 'b'], '✊'),
  b('gr2', 'blends', 'gr', ['We', 'can', 'grab', 'in', 'the', 'sun'], 2, 'grin', ['gr', 'i', 'n'], '😁'),
  b('tr1', 'blends', 'tr', ['It', 'is', 'a', 'trim'], 3, 'trap', ['tr', 'a', 'p'], '🪤'),
  b('tr2', 'blends', 'tr', ['Do', 'not', 'trap', 'on', 'it'], 2, 'trip', ['tr', 'i', 'p'], '🧳'),
  b('st2', 'blends', 'st', ['Spidey', 'will', 'nest', 'on', 'the', 'bed'], 2, 'rest', ['r', 'e', 's', 't'], '😴'),
  b('st3', 'blends', 'st', ['Stem', 'the', 'bad', 'man'], 0, 'stop', ['st', 'o', 'p'], '🛑'),
  b('nd2', 'blends', 'nd', ['My', 'band', 'is', 'in', 'the', 'mud'], 1, 'hand', ['h', 'a', 'n', 'd'], '✋'),
  b('nd3', 'blends', 'nd', ['The', 'jet', 'will', 'sand', 'on', 'it'], 3, 'land', ['l', 'a', 'n', 'd'], '🛬'),
  b('mp1', 'blends', 'mp', ['Spidey', 'can', 'lump', 'on', 'it'], 2, 'jump', ['j', 'u', 'm', 'p'], '🦘'),
  b('mp2', 'blends', 'mp', ['Put', 'the', 'limp', 'on', 'the', 'box'], 2, 'lamp', ['l', 'a', 'm', 'p'], '💡'),

  // ---- Silent-e (magic e) ---- (Chapter 4: Sandman)
  b('ae1', 'vce', 'a_e', ['The', 'cave', 'is', 'for', 'my', 'Dad'], 1, 'cake', ['c', 'a', 'k', 'e'], '🎂'),
  b('ie1', 'vce', 'i_e', ['I', 'ride', 'my', 'kite', 'up', 'the', 'hill'], 3, 'bike', ['b', 'i', 'k', 'e'], '🚲'),
  b('ae2', 'vce', 'a_e', ['His', 'cake', 'is', 'red'], 1, 'cape', ['c', 'a', 'p', 'e'], '🦸'),
  b('ae3', 'vce', 'a_e', ['The', 'gate', 'is', 'so', 'fun'], 1, 'game', ['g', 'a', 'm', 'e'], '🎮'),
  b('ae4', 'vce', 'a_e', ['The', 'cave', 'is', 'by', 'my', 'home'], 1, 'lake', ['l', 'a', 'k', 'e'], '🏞️'),
  b('ie2', 'vce', 'i_e', ['The', 'bite', 'is', 'up'], 1, 'kite', ['k', 'i', 't', 'e'], '🪁'),
  b('ie3', 'vce', 'i_e', ['I', 'side', 'my', 'bike'], 1, 'ride', ['r', 'i', 'd', 'e'], '🚴'),
  b('ie4', 'vce', 'i_e', ['It', 'is', 'five', 'to', 'win'], 2, 'time', ['t', 'i', 'm', 'e'], '⏰'),
  b('oe1', 'vce', 'o_e', ['Spidey', 'ran', 'hole'], 2, 'home', ['h', 'o', 'm', 'e'], '🏠'),
  b('oe2', 'vce', 'o_e', ['The', 'dog', 'has', 'the', 'note', 'in', 'it'], 4, 'bone', ['b', 'o', 'n', 'e'], '🦴'),
  b('oe3', 'vce', 'o_e', ['Spidey', 'has', 'the', 'rode', 'on', 'it'], 3, 'rope', ['r', 'o', 'p', 'e'], '🪢'),
  b('oe4', 'vce', 'o_e', ['The', 'cone', 'is', 'by', 'the', 'den'], 1, 'hole', ['h', 'o', 'l', 'e'], '🕳️'),
  b('ue1', 'vce', 'u_e', ['Pick', 'up', 'the', 'cute'], 3, 'cube', ['c', 'u', 'b', 'e'], '🧊'),
  b('ue2', 'vce', 'u_e', ['The', 'mule', 'is', 'in', 'the', 'tub'], 1, 'tube', ['t', 'u', 'b', 'e'], '🛟'),
  b('ue3', 'vce', 'u_e', ['The', 'pup', 'is', 'cube'], 3, 'cute', ['c', 'u', 't', 'e'], '🥰'),

  // ---- Vowel teams ---- (Chapter 5: Electro)
  b('vt1', 'vowel_teams', 'ai', ['The', 'big', 'pain', 'made', 'mud'], 2, 'rain', ['r', 'ai', 'n'], '🌧️'),
  b('vt2', 'vowel_teams', 'ay', ['We', 'play', 'all', 'way', 'in', 'the', 'sun'], 3, 'day', ['d', 'ay'], '☀️'),
  b('vt3', 'vowel_teams', 'ee', ['My', 'feed', 'are', 'wet', 'in', 'the', 'rain'], 1, 'feet', ['f', 'ee', 't'], '🦶'),
  b('vt4', 'vowel_teams', 'oa', ['The', 'red', 'coat', 'is', 'on', 'the', 'sea'], 2, 'boat', ['b', 'oa', 't'], '🚣'),
  b('vt5', 'vowel_teams', 'ea', ['We', 'are', 'a', 'meat'], 3, 'team', ['t', 'ea', 'm'], '🤝'),
  b('vt6', 'vowel_teams', 'ea', ['I', 'can', 'seat', 'this'], 2, 'read', ['r', 'ea', 'd'], '📖'),
  b('vt7', 'vowel_teams', 'ow_o', ['We', 'play', 'in', 'the', 'show', 'all', 'day'], 4, 'snow', ['sn', 'ow'], '❄️'),
  b('vt8', 'vowel_teams', 'ow_o', ['The', 'seed', 'will', 'glow', 'in', 'the', 'sun'], 3, 'grow', ['gr', 'ow'], '🌱'),
  b('vt9', 'vowel_teams', 'ai', ['The', 'mail', 'on', 'the', 'dog', 'is', 'big'], 1, 'tail', ['t', 'ai', 'l'], '🐕'),

  // ---- R-controlled ---- (Chapter 6: Vulture)
  b('rc1', 'r_controlled', 'ar', ['The', 'red', 'jar', 'is', 'fast'], 2, 'car', ['c', 'ar'], '🚗'),
  b('rc2', 'r_controlled', 'or', ['Blow', 'the', 'corn', 'in', 'the', 'band'], 2, 'horn', ['h', 'or', 'n'], '📯'),
  b('rc3', 'r_controlled', 'ir', ['The', 'big', 'herd', 'sat', 'on', 'the', 'web'], 2, 'bird', ['b', 'ir', 'd'], '🐦'),
  b('rc4', 'r_controlled', 'er', ['The', 'herd', 'is', 'green'], 1, 'fern', ['f', 'er', 'n'], '🌿'),
  b('rc5', 'r_controlled', 'ur', ['Spidey', 'can', 'burn', 'on', 'the', 'wave'], 2, 'surf', ['s', 'ur', 'f'], '🏄'),
  b('rc6', 'r_controlled', 'ar', ['Gwen', 'is', 'a', 'car'], 3, 'star', ['st', 'ar'], '⭐'),
  b('rc7', 'r_controlled', 'or', ['The', 'corn', 'is', 'by', 'my', 'dish'], 1, 'fork', ['f', 'or', 'k'], '🍴'),
  b('rc8', 'r_controlled', 'ur', ['It', 'is', 'my', 'burn', 'to', 'run'], 3, 'turn', ['t', 'ur', 'n'], '🔄'),

  // ---- Diphthongs & more vowel teams ---- (Chapter 7: Venom — finale)
  b('di1', 'diphthongs', 'oo_long', ['The', 'big', 'round', 'boot', 'is', 'up'], 3, 'moon', ['m', 'oo', 'n'], '🌙'),
  b('di2', 'diphthongs', 'oy', ['A', 'toy', 'can', 'run', 'fast'], 1, 'boy', ['b', 'oy'], '🏃'),
  b('di3', 'diphthongs', 'ow_ou', ['A', 'owl', 'is', 'on', 'the', 'road'], 1, 'cow', ['c', 'ow'], '🐄'),
  b('di4', 'diphthongs', 'aw', ['A', 'dog', 'has', 'a', 'saw', 'not', 'a', 'fin'], 4, 'paw', ['p', 'aw'], '🐾'),
  b('di5', 'diphthongs', 'oi', ['Put', 'the', 'coil', 'in', 'the', 'box'], 2, 'coin', ['c', 'oi', 'n'], '🪙'),
  b('di6', 'diphthongs', 'ou', ['The', 'sound', 'is', 'so', 'big'], 1, 'cloud', ['cl', 'ou', 'd'], '☁️'),
  b('di7', 'diphthongs', 'oo_short', ['We', 'read', 'a', 'boot', 'in', 'bed'], 3, 'book', ['b', 'oo', 'k'], '📚'),
  b('di8', 'diphthongs', 'ow_ou', ['Venom', 'is', 'in', 'the', 'down', 'now'], 4, 'town', ['t', 'ow', 'n'], '🏙️'),
  b('di9', 'diphthongs', 'oo_long', ['We', 'will', 'room', 'down'], 2, 'zoom', ['z', 'oo', 'm'], '💨'),
  b('di10', 'diphthongs', 'aw', ['The', 'draw', 'is', 'on', 'his', 'arm'], 1, 'claw', ['cl', 'aw'], '🦀'),
];

/** Every unique word across all beats (sign words + fix words) — for the audio manifest. */
export function beatVocab(): string[] {
  const set = new Set<string>();
  for (const beat of BEATS) {
    beat.words.forEach((w) => set.add(w));
    set.add(beat.correctWord);
  }
  return [...set];
}
